import React, { useState, useEffect, useContext, useCallback } from 'react';
import Switch from '@mui/material/Switch';
import { SocketContext } from '../../../App';
import WindowLayout from '../WindowLayout/WindowLayout';
import useInput from '../../../hooks/useInput';
import { GAME_MODE_DEFS } from '../../GameModes/ModeSelector';
import PasswordField from '../../UI/PasswordField';
import styles from './AddServer.module.css';

const copyText = text => {
    try {
        if (navigator?.clipboard?.writeText) return navigator.clipboard.writeText(text);
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
    } catch {}
};

const PlayerUrlRow = ({ label, url, onJoin }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => { copyText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    return (
        <div style={{ marginBottom: 10, width: '100%' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 3 }}>{label}</div>
            <div style={{ display: 'flex', gap: 6 }}>
                <input readOnly value={url} onFocus={e => e.target.select()}
                    style={{ flex: 1, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: 'white', padding: '6px 10px', fontSize: 12, outline: 'none', cursor: 'text' }}
                />
                <button type="button" onClick={handleCopy}
                    style={{ background: copied ? '#22c55e' : 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: 'white', padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}
                >{copied ? '✓ Copied' : 'Copy'}</button>
                {onJoin && (
                    <button type="button" onClick={onJoin}
                        style={{ background: 'rgba(99,102,241,0.7)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: 'white', padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >Join</button>
                )}
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────
const AddServer = ({ defaultGameMode = 'classic', onCreated }) => {
    const socket = useContext(SocketContext);
    const [isPrivate, setIsPrivate] = useState(false);
    const [vsBot, setVsBot] = useState(false);
    const [isIncorrect, setIsIncorrect] = useState(false);
    const [maxPlayers, setMaxPlayers] = useState(defaultGameMode === 'team_2v2' ? 4 : 2);
    const [gameMode] = useState(defaultGameMode); // mode was chosen in step 1 — not editable here
    const [timerMinutes, setTimerMinutes] = useState(2);
    const serverName = useInput('');
    const password = useInput('');
    const [playerNames, setPlayerNames] = useState(['', '']);
    const [playerMobiles, setPlayerMobiles] = useState(['', '']);
    const [playerEmails, setPlayerEmails] = useState(['', '']);
    const [createdRoom, setCreatedRoom] = useState(null);

    const modeDef = GAME_MODE_DEFS.find(m => m.value === gameMode) || GAME_MODE_DEFS[0];

    useEffect(() => {
        setPlayerNames(prev => Array.from({ length: maxPlayers }, (_, i) => prev[i] ?? ''));
        setPlayerMobiles(prev => Array.from({ length: maxPlayers }, (_, i) => prev[i] ?? ''));
        setPlayerEmails(prev => Array.from({ length: maxPlayers }, (_, i) => prev[i] ?? ''));
    }, [maxPlayers]);

    useEffect(() => {
        if (typeof socket?.on !== 'function') return undefined;
        const handler = data => setCreatedRoom(data);
        socket.on('room:created', handler);
        return () => {
            if (typeof socket?.off === 'function') socket.off('room:created', handler);
        };
    }, [socket]);

    const handlePlayerNameChange = (i, v) =>
        setPlayerNames(prev => prev.map((n, idx) => (idx === i ? v : n)));
    const handlePlayerMobileChange = (i, v) =>
        setPlayerMobiles(prev => prev.map((n, idx) => (idx === i ? v : n)));
    const handlePlayerEmailChange = (i, v) =>
        setPlayerEmails(prev => prev.map((n, idx) => (idx === i ? v : n)));

    const handleButtonClick = e => {
        e.preventDefault();
        const hostName = playerNames[0].trim() || localStorage.getItem('playerName') || '';
        if (!serverName.value || !hostName) { setIsIncorrect(true); return; }
        if (typeof socket?.emit !== 'function') {
            setIsIncorrect(true);
            return;
        }
        localStorage.setItem('playerName', hostName);
        setCreatedRoom(null);
        if (vsBot) {
            socket.emit('player:createBotGame', {
                name: hostName,
                maxPlayers: Number(maxPlayers),
                gameMode,
                timerMinutes: gameMode === 'timer' ? timerMinutes : 0,
            });
        } else {
            socket.emit('room:create', {
                name: serverName.value,
                password: password.value,
                private: isPrivate,
                maxPlayers: Number(maxPlayers),
                gameMode,
                timerMinutes: gameMode === 'timer' ? timerMinutes : 0,
                createdBy: hostName,
                playerNames: playerNames.map(n => n.trim()),
                playerMobiles: playerMobiles.map(m => m.trim()),
                playerEmails: playerEmails.map(e => e.trim()),
            });
        }
    };

    const buildJoinUrl = useCallback((roomId, token) => {
        const base = `${window.location.origin}/join/${roomId}`;
        const params = new URLSearchParams();
        if (token)  params.set('token', token);
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
    }, []);

    const handleJoinAsHost = () => {
        if (!createdRoom) return;
        const slot = createdRoom.joinSlots?.[0];
        window.location.href = buildJoinUrl(
            createdRoom.roomId,
            slot?.token || ''
        );
    };

    const roomUrls = createdRoom
        ? playerNames.map((name, i) => {
            const slot = createdRoom.joinSlots?.[i];
            return buildJoinUrl(
                createdRoom.roomId,
                slot?.token || ''
            );
        })
        : [];

    const form = createdRoom && !vsBot ? (
        /* ── URL-share panel ── */
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
            <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                ✓ Room &ldquo;{createdRoom.name}&rdquo; created!
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span style={{ background: `${modeDef.color}33`, border: `1px solid ${modeDef.color}55`, borderRadius: 20, padding: '3px 10px', fontSize: 11, color: 'white' }}>
                    {modeDef.emoji} {modeDef.label}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{createdRoom.maxPlayers} players</span>
                {createdRoom.gameMode === 'timer' && (
                    <span style={{ color: '#67e8f9', fontSize: 11, fontWeight: 700 }}>⏱ {createdRoom.timerMinutes || timerMinutes} min</span>
                )}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 10 }}>
                Share these links — each player has a unique auto-filled link:
            </div>
            {roomUrls.map((url, i) => (
                <PlayerUrlRow
                    key={i}
                    label={`Player ${i + 1}${i === 0 ? ' (Host / You)' : ''}: ${playerNames[i].trim() || `Player ${i + 1}`}`}
                    url={url}
                    onJoin={i === 0 ? handleJoinAsHost : undefined}
                />
            ))}
            <button type="button" onClick={() => setCreatedRoom(null)}
                style={{ marginTop: 8, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: 'white', padding: '8px 16px', fontSize: 13, cursor: 'pointer', width: '100%' }}
            >← Create another room</button>
        </div>
    ) : (
        /* ── Create-room form ── */
        <form className={styles.formContainer}>
            {/* Current mode badge */}
            <div style={{ width: '100%', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: `${modeDef.color}18`, border: `1px solid ${modeDef.color}44`, borderRadius: 10 }}>
                <span style={{ fontSize: 20 }}>{modeDef.emoji}</span>
                <div>
                    <div style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>{modeDef.label}</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{modeDef.en}</div>
                </div>
            </div>

            {gameMode === 'timer' && (
                <div style={{ width: '100%', marginBottom: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(103,232,249,0.28)' }}>
                    <div style={{ color: '#a5f3fc', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', marginBottom: 8 }}>MATCH TIMER</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 7 }}>
                        {[2, 4, 6, 8, 10].map(minutes => (
                            <button
                                key={minutes}
                                type="button"
                                onClick={() => setTimerMinutes(minutes)}
                                style={{
                                    height: 42,
                                    borderRadius: 9,
                                    border: timerMinutes === minutes ? '2px solid #facc15' : '1px solid rgba(255,255,255,0.18)',
                                    background: timerMinutes === minutes ? 'linear-gradient(135deg,#0891b2,#06b6d4)' : 'rgba(255,255,255,0.06)',
                                    color: '#fff',
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    boxShadow: 'none',
                                }}
                            >{minutes}m</button>
                        ))}
                    </div>
                    <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.55)', fontSize: 10.5, lineHeight: 1.45 }}>
                        Highest total pawn progress wins. Equal total → highest single pawn wins.
                    </div>
                </div>
            )}

            <input type='text' placeholder='Room Name' {...serverName}
                style={{ border: isIncorrect && !serverName.value ? '1px solid red' : '1px solid white' }}
            />

            <div className={styles.privateContainer}>
                <label>vs Bot</label>
                <Switch checked={vsBot} color='secondary' onChange={() => setVsBot(v => !v)} />
            </div>

            {!vsBot && (
                <div className={styles.privateContainer}>
                    <label>Private</label>
                    <Switch checked={isPrivate} color='primary' onChange={() => setIsPrivate(v => !v)} />
                </div>
            )}

            {!vsBot && isPrivate && (
                <PasswordField placeholder='Password (optional)' {...password} />
            )}

            <div className={styles.playerSelectContainer}>
                <label>{vsBot ? 'Players (you + bots):' : 'Max Players:'}</label>
                <select value={maxPlayers} onChange={e => setMaxPlayers(Number(e.target.value))}
                    disabled={gameMode === 'team_2v2'}
                >
                    <option value="2">2 {vsBot ? '(1v1 Bot)' : 'Players'}</option>
                    <option value="3">3 {vsBot ? '(1v2 Bots)' : 'Players'}</option>
                    <option value="4">4 {vsBot ? '(1v3 Bots)' : 'Players'}</option>
                </select>
            </div>

            {/* Player name / mobile / email inputs */}
            {!vsBot && (
                <div style={{ width: '100%', marginTop: 10 }}>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 6 }}>Player details (for join links):</div>
                    {playerNames.map((name, i) => (
                        <div key={i} style={{ marginBottom: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6 }}>
                                Player {i + 1}{i === 0 ? ' (Host)' : ''}
                            </div>
                            <input type='text' value={name}
                                onChange={e => handlePlayerNameChange(i, e.target.value)}
                                placeholder={i === 0 ? 'Name (host)' : `Player ${i + 1} name`}
                                style={{ marginBottom: 4, border: isIncorrect && i === 0 && !name.trim() ? '1px solid red' : '1px solid rgba(255,255,255,0.3)' }}
                            />
                            <input type='text' value={playerMobiles[i] || ''}
                                onChange={e => handlePlayerMobileChange(i, e.target.value)}
                                placeholder='Mobile (optional)'
                                style={{ marginBottom: 4, border: '1px solid rgba(255,255,255,0.3)' }}
                            />
                            <input type='text' value={playerEmails[i] || ''}
                                onChange={e => handlePlayerEmailChange(i, e.target.value)}
                                placeholder='Email (optional)'
                                style={{ border: '1px solid rgba(255,255,255,0.3)' }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {vsBot && (
                <input type='text' value={playerNames[0] || ''} onChange={e => handlePlayerNameChange(0, e.target.value)}
                    placeholder='Your name'
                    style={{ marginTop: 8, border: isIncorrect && !playerNames[0]?.trim() ? '1px solid red' : '1px solid rgba(255,255,255,0.5)' }}
                />
            )}

            <button onClick={handleButtonClick}>
                {vsBot ? 'Play vs Bot' : 'Create Room & Get Links'}
            </button>
        </form>
    );

    // When used standalone (not inside a host modal step), wrap in WindowLayout
    if (!onCreated) {
        return <WindowLayout title='Host A Server' content={form} />;
    }
    return form;
};

export default AddServer;
